import { useCallback, useState } from 'react';

/**
 * Where the R/G/B buttons live on a waiting kart.
 *
 * 'row'  — always on their own second row. One tap to assign, at the cost of
 *          height, so fewer karts fit the pool.
 * 'tap'  — hidden until the card is tapped. Two taps to assign, but the queue
 *          stays compact when several karts are waiting at once.
 */
export type AssignLayout = 'row' | 'tap';

const STORAGE_KEY = 'karting-assign-layout';

export function storedAssignLayout(): AssignLayout {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'tap' ? 'tap' : 'row';
  } catch {
    // Private mode throws rather than returning null.
    return 'row';
  }
}

/** Remembered per device: the pit-wall tablet and a laptop want different things. */
export function useAssignLayout(): {
  layout: AssignLayout;
  toggle: () => void;
} {
  const [layout, setLayout] = useState<AssignLayout>(storedAssignLayout);

  const toggle = useCallback(() => {
    setLayout(current => {
      const next: AssignLayout = current === 'row' ? 'tap' : 'row';
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // Not being able to remember it is no reason to refuse the switch.
      }
      return next;
    });
  }, []);

  return { layout, toggle };
}
